import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { getPollingSetups, updatePollingSetups } from '@/api/polling-setup'
import IApiResponse from '@/types/api-response'
import { IPollingSetupProps } from '@/pages/Settings/PollingSetup/compoments/EditForm'

interface PollingSetupState {
	polling_setups: IPollingSetup[]
	loading: boolean
	updating: boolean
	error?: string | null
}

export interface IPollingSetup {
	_id: string
	min_remaining: number
	max_remaining: number
	polling_interval: number
	description: string | null
	created_at: string
	updated_at: string | null
}

const initialState: PollingSetupState = {
	loading: false,
	polling_setups: [],
	updating: false,
}

export const fetchPollingSetups = createAsyncThunk<
	IApiResponse<IPollingSetup[]>,
	undefined
>('pooling_setup/fetch_polling_setup', async () => {
	return await getPollingSetups()
})

export const updatePollingSetup = createAsyncThunk<
	IApiResponse<null | undefined>,
	{ params: IPollingSetupProps[]; cb?: (result: IApiResponse<null>) => void }
>(
	'pooling_setup/update_pooling_setup',
	async ({
		params,
		cb,
	}: {
		params: IPollingSetupProps[]
		cb?: (result: IApiResponse<null>) => void
	}) => {
		try {
			const result = await updatePollingSetups(params)

			if (typeof cb === 'function') {
				cb(result)
			}

			return result
		} catch (error) {
			if (typeof cb === 'function') {
				cb({ success: false })
			}

			throw error
		}
	}
)

const pollingSetupSlice = createSlice({
	name: 'pooling_setup',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchPollingSetups.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(fetchPollingSetups.fulfilled, (state, { payload }) => {
				state.loading = false
				if (payload.success) {
					state.polling_setups = payload.data!
				} else {
					state.polling_setups = []
				}
			})
			.addCase(fetchPollingSetups.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message || 'Something went wrong'
			})

			.addCase(updatePollingSetup.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(updatePollingSetup.fulfilled, (state, { payload }) => {
				state.loading = false
			})
			.addCase(updatePollingSetup.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message || 'Something went wrong'
			})
	},
})

export default pollingSetupSlice.reducer
